import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Users, BookOpen, UserCheck, UsersRound, TrendingUp, AlertCircle, ChevronLeft, ChevronRight, Check, X, StickyNote, History, Trash2, AlertTriangle, Phone, UserPlus, UserMinus, Calendar as CalendarIcon, GripVertical, Settings, MapPin, Clock, Heart, Sun, Moon, Info } from 'lucide-react';
import { toast } from 'sonner';
import ChildAvatar from '@/components/ChildAvatar';
import { useAuth } from '@/contexts/AuthContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Dashboard config - inclui birthdays por padrão
  const [dashboardConfig, setDashboardConfig] = useState({
    visible_boxes: ["schedule", "birthdays", "calendar", "tasks", "absence_alerts", "movements"],
    box_order: ["schedule", "birthdays", "calendar", "tasks", "absence_alerts", "movements"]
  });
  
  // Tasks Post-it state
  const [tasks, setTasks] = useState([]);
  const [newTaskText, setNewTaskText] = useState('');
  const [tasksLoading, setTasksLoading] = useState(true);
  const [showCompletedHistory, setShowCompletedHistory] = useState(false);
  const [completedDates, setCompletedDates] = useState([]);
  const [selectedHistoryDate, setSelectedHistoryDate] = useState('');
  const [historyTasks, setHistoryTasks] = useState([]);
  
  // Absence alerts state
  const [absenceAlerts, setAbsenceAlerts] = useState([]);
  const [absenceAlertsLoading, setAbsenceAlertsLoading] = useState(true);
  const [selectedAbsenceChild, setSelectedAbsenceChild] = useState(null);
  
  // Movements state
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(true);
  const [movementsFilter, setMovementsFilter] = useState('both');
  const [selectedMovementChild, setSelectedMovementChild] = useState(null);
  const [childDetailsLoading, setChildDetailsLoading] = useState(false);
  
  // Calendar event detail
  const [selectedDateEvents, setSelectedDateEvents] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Schedule state (escala do dia)
  const WEEKDAYS = ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
  const WEEKDAY_LABELS = { segunda: 'Segunda-feira', terca: 'Terça-feira', quarta: 'Quarta-feira', quinta: 'Quinta-feira', sexta: 'Sexta-feira' };
  const [scheduleDay, setScheduleDay] = useState(() => {
    const dow = new Date().getDay(); // 0=dom,1=seg...
    const map = { 1: 'segunda', 2: 'terca', 3: 'quarta', 4: 'quinta', 5: 'sexta' };
    return map[dow] || 'segunda';
  });
  const [scheduleData, setScheduleData] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Admin config mode - only for configuring OTHER users' dashboards
  const [isEditMode, setIsEditMode] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editingUserConfig, setEditingUserConfig] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Box definitions - TODOS os boxes disponíveis para configuração
  const boxDefinitions = {
    schedule: { label: 'Escala do Dia', description: 'Escala de trabalho do dia', fullWidth: true },
    birthdays: { label: 'Aniversariantes', description: 'Aniversariantes do mês', fullWidth: false },
    calendar: { label: 'Calendário', description: 'Eventos e agenda', fullWidth: false },
    tasks: { label: 'Tarefas do Dia', description: 'Post-it de tarefas', fullWidth: false },
    absence_alerts: { label: 'Alerta de Faltas', description: 'Crianças com faltas consecutivas', fullWidth: false },
    movements: { label: 'Movimentações', description: 'Cadastros e inativações recentes', fullWidth: false }
  };

  // Lista fixa de todos os boxes para garantir ordem
  const ALL_BOX_IDS = ['schedule', 'birthdays', 'calendar', 'tasks', 'absence_alerts', 'movements'];

  useEffect(() => {
    fetchDashboardConfig();
    fetchStats();
    fetchEvents();
    fetchTasks();
    fetchAbsenceAlerts();
    fetchMovements();
    fetchDailySchedule();
    fetchProfessionals();
  }, []);

  useEffect(() => {
    fetchMovements();
  }, [movementsFilter]);

  const fetchDashboardConfig = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/my-config`);
      const config = response.data;
      
      // Filtrar IDs inválidos e garantir que todos os boxes válidos estejam na ordem
      const validOrder1 = [...new Set((config.box_order || []).filter(id => ALL_BOX_IDS.includes(id)))];
      const missingBoxes1 = ALL_BOX_IDS.filter(id => !validOrder1.includes(id));
      config.box_order = [...validOrder1, ...missingBoxes1];
      
      // Filtrar visible_boxes para conter apenas IDs válidos e desduplicar
      config.visible_boxes = [...new Set((config.visible_boxes || []).filter(id => ALL_BOX_IDS.includes(id)))];
      
      // Se não houver boxes visíveis, mostrar todos
      if (config.visible_boxes.length === 0) {
        config.visible_boxes = ALL_BOX_IDS;
      }
      
      setDashboardConfig(config);
    } catch (error) {
      console.log('Usando configuração padrão do dashboard');
      // Configuração padrão com TODOS os boxes
      setDashboardConfig({
        visible_boxes: ALL_BOX_IDS,
        box_order: ALL_BOX_IDS
      });
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar eventos');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`);
      setTasks(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar tarefas');
    } finally {
      setTasksLoading(false);
    }
  };

  const fetchCompletedDates = async () => {
    try {
      const response = await axios.get(`${API}/tasks/completed-dates`);
      setCompletedDates(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedHistoryDate(response.data[0]);
        fetchHistoryTasks(response.data[0]);
      }
    } catch (error) {
      console.log('Erro ao carregar datas');
    }
  };

  const fetchHistoryTasks = async (date) => {
    try {
      const response = await axios.get(`${API}/tasks?show_completed=true&date=${date}`);
      setHistoryTasks(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar histórico');
    }
  };

  const fetchAbsenceAlerts = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/absence-alerts`);
      setAbsenceAlerts(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar alertas de faltas');
    } finally {
      setAbsenceAlertsLoading(false);
    }
  };

  const toggleAbsenceContact = async (childId, currentStatus) => {
    try {
      if (currentStatus === null || currentStatus === undefined) {
        // Sem contato → verde (sucesso)
        await axios.post(`${API}/dashboard/absence-alerts/${childId}/contact`, { status: 'success' });
      } else if (currentStatus === 'success') {
        // Verde → vermelho (falhou)
        await axios.post(`${API}/dashboard/absence-alerts/${childId}/contact`, { status: 'failed' });
      } else {
        // Vermelho → desmarcar
        await axios.delete(`${API}/dashboard/absence-alerts/${childId}/contact`);
      }
      fetchAbsenceAlerts();
    } catch (error) {
      console.log('Erro ao atualizar contato');
    }
  };

  const fetchMovements = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/movements?filter_type=${movementsFilter}`);
      setMovements(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar movimentações');
    } finally {
      setMovementsLoading(false);
    }
  };

  const fetchDailySchedule = async (day) => {
    const targetDay = day || scheduleDay;
    try {
      const response = await axios.get(`${API}/dashboard/schedule/${targetDay}`);
      setScheduleData(response.data);
    } catch (error) {
      // Fallback: tenta endpoint sem dia (dia atual)
      try {
        const res = await axios.get(`${API}/dashboard/schedule`);
        setScheduleData(res.data);
      } catch (e) {
        console.log('Erro ao carregar escala');
      }
    }
  };

  const fetchProfessionals = async () => {
    try {
      const response = await axios.get(`${API}/professionals`);
      setProfessionals(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar profissionais');
    }
  };

  const saveTimeoutRef = useRef({});
  
  const autoSaveScheduleField = async (day, shift, section, field, value) => {
    if (!isAdmin()) return;
    // Atualizar estado local imediatamente
    setScheduleData(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      updated[shift] = { ...updated[shift] };
      updated[shift][section] = { ...updated[shift][section], [field]: value };
      return updated;
    });
    // Debounce: salvar no backend após 500ms sem digitação
    const key = `${day}_${shift}_${section}`;
    if (saveTimeoutRef.current[key]) clearTimeout(saveTimeoutRef.current[key]);
    saveTimeoutRef.current[key] = setTimeout(async () => {
      setScheduleSaving(true);
      try {
        // Pegar dados mais recentes do state
        const freshData = await axios.get(`${API}/dashboard/schedule/${day}`);
        const currentTasks = freshData.data?.[shift]?.[section] || {};
        await axios.put(`${API}/dashboard/schedule/${day}/${shift}/${section}`, {
          tasks: { ...currentTasks, [field]: value }
        });
      } catch (error) {
        console.log('Erro ao salvar escala');
      } finally {
        setScheduleSaving(false);
      }
    }, 500);
  };

  const navigateScheduleDay = (delta) => {
    const idx = WEEKDAYS.indexOf(scheduleDay);
    const newIdx = (idx + delta + WEEKDAYS.length) % WEEKDAYS.length;
    const newDay = WEEKDAYS[newIdx];
    setScheduleDay(newDay);
    fetchDailySchedule(newDay);
  };

  // Buscar detalhes completos de uma criança para o modal
  const fetchChildDetails = async (childId) => {
    setChildDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/children/${childId}`);
      setSelectedMovementChild(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da criança');
      setSelectedMovementChild(null);
    } finally {
      setChildDetailsLoading(false);
    }
  };

  // Formatar dias da semana para exibição
  const formatDaysOfWeek = (days) => {
    if (!days || !Array.isArray(days) || days.length === 0) return '-';
    
    const dayLabels = {
      'segunda': 'Seg',
      'terca': 'Ter',
      'quarta': 'Qua',
      'quinta': 'Qui',
      'sexta': 'Sex'
    };
    
    // Verificar padrões comuns
    const sorted = days.map(d => d.toLowerCase()).sort();
    if (sorted.length === 5) return 'Seg a Sex';
    if (sorted.length === 2 && sorted.includes('terca') && sorted.includes('quinta')) return 'Ter e Qui';
    if (sorted.length === 2 && sorted.includes('quarta') && sorted.includes('sexta')) return 'Qua e Sex';
    
    return days.map(d => dayLabels[d.toLowerCase()] || d).join(', ');
  };

  // Formatar origem para exibição
  const formatOrigin = (origin) => {
    const labels = {
      'livre_demanda': 'Livre Demanda',
      'gov_celso_ramos': 'Governador Celso Ramos',
      'biguacu': 'Biguaçu'
    };
    return labels[origin] || origin || '-';
  };

  // Task handlers
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskText.trim()) return;
    
    try {
      const response = await axios.post(`${API}/tasks`, { text: newTaskText.trim() });
      setTasks(prev => [...prev, response.data]);
      setNewTaskText('');
    } catch (error) {
      toast.error('Erro ao criar tarefa');
    }
  };

  const handleToggleTask = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      toast.error('Erro ao atualizar tarefa');
    }
  };

  const handleMarkIrrelevant = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: 'irrelevant' });
      setTasks(prev => prev.filter(t => t.id !== task.id));
      toast.success('Tarefa arquivada');
    } catch (error) {
      toast.error('Erro ao arquivar tarefa');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      toast.error('Erro ao deletar tarefa');
    }
  };

  // Handle drag end for dashboard boxes
  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setEditingUserConfig(prev => {
        const oldIndex = prev.box_order.indexOf(active.id);
        const newIndex = prev.box_order.indexOf(over.id);
        const newOrder = arrayMove(prev.box_order, oldIndex, newIndex);
        return { ...prev, box_order: newOrder };
      });
    }
  }, []);

  // Fetch all users for admin to configure their dashboards
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.log('Erro ao carregar usuários');
    }
  };

  // Load user dashboard config for editing
  const loadUserConfig = async (userId) => {
    try {
      const response = await axios.get(`${API}/dashboard/config/${userId}`);
      const config = response.data;
      
      // Filtrar IDs inválidos e garantir que todos os boxes válidos estejam na ordem
      const validOrder2 = [...new Set((config.box_order || []).filter(id => ALL_BOX_IDS.includes(id)))];
      const missingBoxes2 = ALL_BOX_IDS.filter(id => !validOrder2.includes(id));
      config.box_order = [...validOrder2, ...missingBoxes2];
      
      // Filtrar visible_boxes para conter apenas IDs válidos e desduplicar
      config.visible_boxes = [...new Set((config.visible_boxes || []).filter(id => ALL_BOX_IDS.includes(id)))];
      
      setEditingUserConfig(config);
      setSelectedUserId(userId);
    } catch (error) {
      // If no config exists, use default with ALL boxes
      setEditingUserConfig({
        user_id: userId,
        visible_boxes: ALL_BOX_IDS,
        box_order: ALL_BOX_IDS
      });
      setSelectedUserId(userId);
    }
  };

  // Save dashboard config for a specific user
  const saveUserDashboardConfig = async () => {
    if (!selectedUserId || !editingUserConfig) return;
    
    try {
      await axios.put(`${API}/dashboard/config/${selectedUserId}`, {
        visible_boxes: editingUserConfig.visible_boxes,
        box_order: editingUserConfig.box_order
      });
      toast.success('Configuração do dashboard salva!');
      setIsEditMode(false);
      setSelectedUserId(null);
      setEditingUserConfig(null);
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  // Toggle box visibility for user being edited
  const toggleBoxVisibility = (boxId) => {
    setEditingUserConfig(prev => {
      const newVisible = prev.visible_boxes.includes(boxId)
        ? prev.visible_boxes.filter(id => id !== boxId)
        : [...prev.visible_boxes, boxId];
      return { ...prev, visible_boxes: newVisible };
    });
  };

  // Open admin config mode
  const openAdminConfig = () => {
    fetchAllUsers();
    setIsEditMode(true);
  };

  const openCompletedHistory = () => {
    setShowCompletedHistory(true);
    fetchCompletedDates();
  };

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const getDayLabel = (day) => {
    const labels = {
      'segunda': 'Seg',
      'terca': 'Ter',
      'quarta': 'Qua',
      'quinta': 'Qui',
      'sexta': 'Sex'
    };
    return labels[day] || day;
  };

  // Birthday date formatter
  const formatBirthday = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}`;
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getEventsForDay = (day) => {
    if (!day) return [];
    const year = currentMonth.getFullYear();
    const month = (currentMonth.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = day.toString().padStart(2, '0');
    const dateStr = `${year}-${month}-${dayStr}`;
    
    return events.filter(event => {
      if (event.start_date && event.end_date) {
        return dateStr >= event.start_date && dateStr <= event.end_date;
      }
      return event.date === dateStr;
    });
  };

  // Event category colors - azul, laranja, marrom conforme solicitado
  const getEventColor = (event) => {
    const category = event.event_category || event.event_type;
    switch (category) {
      case 'feriado':
        return 'bg-amber-700'; // Feriado - marrom
      case 'sem_atendimento':
        return 'bg-orange-500'; // Sem atendimento - laranja
      case 'com_atendimento':
      default:
        return 'bg-blue-500'; // Com atendimento - azul
    }
  };

  const getEventColorLight = (event) => {
    const category = event.event_category || event.event_type;
    switch (category) {
      case 'feriado':
        return 'bg-amber-100 text-amber-900 border-amber-400'; // Feriado - marrom
      case 'sem_atendimento':
        return 'bg-orange-100 text-orange-800 border-orange-300'; // Sem atendimento - laranja
      case 'com_atendimento':
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300'; // Com atendimento - azul
    }
  };

  const getEventCategoryLabel = (event) => {
    const category = event.event_category || event.event_type;
    switch (category) {
      case 'feriado':
        return 'Feriado';
      case 'sem_atendimento':
        return 'Sem atendimento';
      case 'com_atendimento':
      default:
        return 'Com atendimento';
    }
  };

  const handleDayClick = (day) => {
    if (!day) return;
    const dayEvents = getEventsForDay(day);
    if (dayEvents.length > 0) {
      setSelectedDate(day);
      setSelectedDateEvents(dayEvents);
    }
  };

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const monthName = currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  // Check if box is visible
  const isBoxVisible = (boxId) => {
    return dashboardConfig.visible_boxes?.includes(boxId) ?? true;
  };

  // Render stats cards
  const renderStatsCards = () => {
    // Stats sempre aparecem, não são configuráveis
    const statCards = [
      { title: 'Crianças Ativas', value: stats?.total_children || 0, icon: Users, color: 'bg-blue-500' },
      { title: 'Atividades', value: stats?.total_activities || 0, icon: BookOpen, color: 'bg-green-500' },
      { title: 'Profissionais', value: stats?.total_professionals || 0, icon: UserCheck, color: 'bg-purple-500' },
      { title: 'Grupos', value: stats?.total_groups || 0, icon: UsersRound, color: 'bg-orange-500' },
    ];

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6" data-testid="stats-cards">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-600">{stat.title}</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-800">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-2 sm:p-3 rounded-full`}>
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render calendar
  const renderCalendar = () => {
    if (!isBoxVisible('calendar')) return null;
    
    const days = getDaysInMonth(currentMonth);
    const today = new Date();
    
    // Get all events for the current month for the footer
    const monthEvents = events.filter(event => {
      const year = currentMonth.getFullYear();
      const month = (currentMonth.getMonth() + 1).toString().padStart(2, '0');
      const monthPrefix = `${year}-${month}`;
      
      if (event.start_date && event.end_date) {
        return event.start_date.startsWith(monthPrefix) || event.end_date.startsWith(monthPrefix);
      }
      return event.date?.startsWith(monthPrefix);
    });

    return (
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="font-semibold text-slate-800 capitalize">{monthName}</h3>
          <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span>Com atendimento</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500"></span>Sem atendimento</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-700"></span>Feriado</span>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const hasEvents = dayEvents.length > 0;
            const isToday = day && 
                            today.getDate() === day && 
                            today.getMonth() === currentMonth.getMonth() && 
                            today.getFullYear() === currentMonth.getFullYear();

            return (
              <div
                key={index}
                onClick={() => handleDayClick(day)}
                className={`
                  aspect-square flex flex-col items-center justify-center rounded-lg text-sm relative
                  ${day ? 'hover:bg-slate-50 cursor-pointer' : ''}
                  ${isToday ? 'bg-[#D87A4E]/10 font-bold text-[#D87A4E]' : ''}
                  ${hasEvents ? 'ring-2 ring-offset-1' : ''}
                  ${hasEvents && dayEvents[0].event_category === 'feriado' ? 'ring-amber-600' : ''}
                  ${hasEvents && dayEvents[0].event_category === 'sem_atendimento' ? 'ring-orange-400' : ''}
                  ${hasEvents && (dayEvents[0].event_category === 'com_atendimento' || !dayEvents[0].event_category) ? 'ring-blue-400' : ''}
                `}
              >
                {day && (
                  <>
                    <span>{day}</span>
                    {hasEvents && (
                      <div className="flex gap-0.5 mt-0.5">
                        {dayEvents.slice(0, 3).map((event, i) => (
                          <div
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${getEventColor(event)}`}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer with event titles */}
        {monthEvents.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100 space-y-1">
            {monthEvents.map((event, idx) => {
              const eventDay = event.date?.split('-')[2] || event.start_date?.split('-')[2] || '';
              return (
                <div 
                  key={idx} 
                  className="flex items-center gap-2 text-xs text-slate-600"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getEventColor(event)}`}></span>
                  <span className="font-medium text-slate-700">{parseInt(eventDay)}</span>
                  <span>–</span>
                  <span className="truncate">{event.title}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render schedule (escala do dia)
  const renderSchedule = () => {
    if (!isBoxVisible('schedule')) return null;

    const canEdit = isAdmin();

    const renderScheduleInput = (day, shift, section, field, label) => {
      const value = scheduleData?.[shift]?.[section]?.[field] || '';
      return (
        <div key={field}>
          <label className="block text-[10px] text-slate-400 mb-0.5">{label}</label>
          {canEdit ? (
            <input
              type="text"
              list="prof-datalist"
              value={value}
              onChange={(e) => autoSaveScheduleField(day, shift, section, field, e.target.value)}
              className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500 bg-white"
              placeholder="—"
            />
          ) : (
            <p className="text-xs text-slate-700 px-1 py-1 min-h-[28px]">{value || <span className="text-slate-300">—</span>}</p>
          )}
        </div>
      );
    };

    const renderSection = (day, shift, section, title, fields, color) => (
      <div className={`rounded-lg border p-2.5 ${color}`}>
        <p className="text-[10px] font-bold uppercase tracking-wider mb-2 text-slate-500">{title}</p>
        <div className="grid grid-cols-3 gap-x-2 gap-y-1">
          {fields.map(f => renderScheduleInput(day, shift, section, f.field, f.label))}
        </div>
      </div>
    );

    const lancheFields = [
      { field: 'refeitorio', label: 'Refeitório' },
      { field: 'apoio', label: 'Apoio' },
    ];
    const refeicaoFields = [
      { field: 'refeitorio', label: 'Refeitório' },
      { field: 'hall', label: 'Hall' },
      { field: 'banheiro', label: 'Banheiro' },
      { field: 'apoio', label: 'Apoio' },
    ];

    const currentDay = scheduleDay;

    return (
      <div 
        className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-md p-4 border border-indigo-200"
        data-testid="schedule-box"
      >
        {/* Datalist profissionais */}
        <datalist id="prof-datalist">
          {professionals.map((p) => <option key={p.id} value={p.name} />)}
        </datalist>

        {/* Header com navegação */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigateScheduleDay(-1)} className="p-1.5 rounded-lg hover:bg-indigo-100 text-indigo-600 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-indigo-600" />
            <h3 className="font-semibold text-indigo-900 text-sm">
              {WEEKDAY_LABELS[currentDay]}
            </h3>
            {scheduleSaving && <span className="text-[10px] text-indigo-400 animate-pulse">salvando...</span>}
          </div>
          <button onClick={() => navigateScheduleDay(1)} className="p-1.5 rounded-lg hover:bg-indigo-100 text-indigo-600 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          {/* MANHÃ */}
          <div>
            <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Sun className="h-3.5 w-3.5" /> Manhã
            </p>
            <div className="space-y-2">
              {/* Chamada da Manhã */}
              <div className="rounded-lg border p-2.5 bg-amber-100/50 border-amber-300">
                {renderScheduleInput(currentDay, 'manha', 'chamada', 'responsavel', 'Chamada')}
              </div>
              {renderSection(currentDay, 'manha', 'lanche', 'Lanche', lancheFields, 'bg-amber-50/60 border-amber-200')}
              {renderSection(currentDay, 'manha', 'refeicao', 'Refeição', refeicaoFields, 'bg-amber-50/40 border-amber-100')}
            </div>
          </div>

          {/* Divisor */}
          <div className="border-t border-indigo-200" />

          {/* TARDE */}
          <div>
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Moon className="h-3.5 w-3.5" /> Tarde
            </p>
            <div className="space-y-2">
              {/* Chamada da Tarde */}
              <div className="rounded-lg border p-2.5 bg-blue-100/50 border-blue-300">
                {renderScheduleInput(currentDay, 'tarde', 'chamada', 'responsavel', 'Chamada')}
              </div>
              {renderSection(currentDay, 'tarde', 'lanche', 'Lanche', lancheFields, 'bg-blue-50/60 border-blue-200')}
              {renderSection(currentDay, 'tarde', 'refeicao', 'Refeição', refeicaoFields, 'bg-blue-50/40 border-blue-100')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render tasks post-it
  const renderTasks = () => {
    if (!isBoxVisible('tasks')) return null;

    return (
      <div 
        className="rounded-xl shadow-lg p-4 sm:p-5 border-2 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)',
          borderColor: '#FACC15',
          boxShadow: '4px 4px 12px rgba(0,0,0,0.1), inset 0 0 30px rgba(255,255,255,0.3)'
        }}
        data-testid="tasks-postit"
      >
        <div 
          className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-16 h-6 rounded-sm opacity-60"
          style={{ background: 'linear-gradient(180deg, #D4A574 0%, #C49A6C 100%)' }}
        />
        
        <div className="flex items-center justify-between mb-3 mt-2">
          <div className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-amber-700" />
            <h3 className="font-semibold text-amber-900 text-base sm:text-lg">Tarefas do Dia</h3>
          </div>
          <button
            onClick={openCompletedHistory}
            className="text-amber-700 hover:text-amber-900 p-1.5 rounded-lg hover:bg-amber-200/50 transition-colors"
            title="Ver tarefas concluídas"
          >
            <History className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleAddTask} className="mb-3">
          <input
            type="text"
            value={newTaskText}
            onChange={(e) => setNewTaskText(e.target.value)}
            placeholder="Digite uma tarefa e pressione Enter..."
            className="w-full px-3 py-2 rounded-lg border border-amber-300 bg-white/70 text-amber-900 placeholder-amber-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            data-testid="task-input"
          />
        </form>

        <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="tasks-list">
          {tasksLoading ? (
            <p className="text-amber-600 text-sm text-center py-4">Carregando...</p>
          ) : tasks.length === 0 ? (
            <p className="text-amber-600 text-sm text-center py-4 italic">
              Nenhuma tarefa para hoje
            </p>
          ) : (
            tasks.map((task) => (
              <div
                key={task.id}
                className={`flex items-center gap-2 p-2 rounded-lg transition-all group ${
                  task.status === 'completed' 
                    ? 'bg-amber-200/40' 
                    : 'bg-white/50 hover:bg-white/70'
                }`}
                data-testid={`task-item-${task.id}`}
              >
                <button
                  onClick={() => handleToggleTask(task)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    task.status === 'completed'
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-amber-400 hover:border-green-500'
                  }`}
                >
                  {task.status === 'completed' && <Check className="h-3 w-3" />}
                </button>
                
                <span 
                  className={`flex-1 text-sm ${
                    task.status === 'completed' 
                      ? 'line-through text-amber-600' 
                      : 'text-amber-900'
                  }`}
                >
                  {task.text}
                </span>
                
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleMarkIrrelevant(task)}
                    className="p-1 text-amber-500 hover:text-amber-700 hover:bg-amber-200/50 rounded"
                    title="Perdeu sentido"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100/50 rounded"
                    title="Excluir"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {tasks.length > 0 && (
          <div className="mt-3 pt-2 border-t border-amber-300/50 text-xs text-amber-700">
            {tasks.filter(t => t.status === 'pending').length} pendente(s) · {tasks.filter(t => t.status === 'completed').length} concluída(s)
          </div>
        )}
      </div>
    );
  };

  // Render absence alerts
  const renderAbsenceAlerts = () => {
    if (!isBoxVisible('absence_alerts')) return null;

    return (
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 border border-slate-200 relative">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 text-base">Alerta de Faltas</h3>
            <p className="text-xs text-slate-500">Crianças com 3+ faltas consecutivas</p>
          </div>
        </div>

        <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="absence-alerts-list">
          {absenceAlertsLoading ? (
            <p className="text-slate-500 text-sm text-center py-4">Carregando...</p>
          ) : absenceAlerts.length === 0 ? (
            <div className="text-center py-6">
              <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-green-600 text-sm font-medium">Nenhum alerta</p>
              <p className="text-slate-400 text-xs">Todas as crianças estão frequentando normalmente</p>
            </div>
          ) : (
            absenceAlerts.map((child) => {
              const alertStyles = {
                unjustified: { bg: 'bg-red-50 hover:bg-red-100', border: 'border-red-200', text: 'text-red-600', badge: 'bg-red-500', avatarBorder: 'border-red-300' },
                justified: { bg: 'bg-orange-50 hover:bg-orange-100', border: 'border-orange-200', text: 'text-orange-600', badge: 'bg-orange-400', avatarBorder: 'border-orange-300' },
                mixed: { bg: 'bg-amber-50 hover:bg-amber-100', border: 'border-amber-200', text: 'text-amber-600', badge: 'bg-amber-500', avatarBorder: 'border-amber-300' }
              };
              const style = alertStyles[child.alert_type] || alertStyles.unjustified;
              
              return (
                <div
                  key={child.id}
                  className={`flex items-center gap-2 p-2 rounded-lg transition-colors border ${style.bg} ${style.border} ${child.contacted ? 'opacity-60' : ''}`}
                >
                  {/* Indicador de contato tristate */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAbsenceContact(child.id, child.contact_status || null);
                    }}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 cursor-pointer transition-colors ${
                      child.contact_status === 'success'
                        ? 'bg-green-500 border-green-600'
                        : child.contact_status === 'failed'
                        ? 'bg-red-500 border-red-600'
                        : 'bg-white border-slate-300 hover:border-green-400'
                    }`}
                    title={
                      child.contact_status === 'success' ? 'Contato bem sucedido (clique: mal sucedido)'
                        : child.contact_status === 'failed' ? 'Contato mal sucedido (clique: desmarcar)'
                        : 'Marcar contato bem sucedido'
                    }
                  >
                    {child.contact_status === 'success' && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {child.contact_status === 'failed' && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                  <div 
                    onClick={() => setSelectedAbsenceChild(child)}
                    className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                  >
                    <ChildAvatar photo={child.photo_url} name={child.name} size="sm" zoomable={true} className={`border ${style.avatarBorder}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-slate-800 text-sm truncate ${child.contacted ? 'line-through' : ''}`}>{child.name}</p>
                      <p className={`text-xs ${style.text}`}>
                        {child.consecutive_absences} faltas
                        {child.alert_type === 'mixed' && <span className="text-slate-500"> · misto</span>}
                        {child.alert_type === 'justified' && <span className="text-slate-500"> · justificadas</span>}
                      </p>
                    </div>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full ${style.badge} text-white text-xs font-bold flex items-center justify-center`}>
                      {child.consecutive_absences}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {absenceAlerts.length > 0 && (
          <div className="mt-3 pt-2 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-3">
            <span>{absenceAlerts.length} criança(s)</span>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span>Não just.</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Mista</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400"></span>Just.</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render movements box
  const renderMovements = () => {
    if (!isBoxVisible('movements')) return null;

    return (
      <>
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 text-base">Movimentações</h3>
                <p className="text-xs text-slate-500">Últimos 7 dias</p>
              </div>
            </div>
            <select
              value={movementsFilter}
              onChange={(e) => setMovementsFilter(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="both">Todos</option>
              <option value="registered">Cadastrados</option>
              <option value="inactivated">Inativados</option>
            </select>
          </div>

          <div className="space-y-2 max-h-52 overflow-y-auto" data-testid="movements-list">
            {movementsLoading ? (
              <p className="text-slate-500 text-sm text-center py-4">Carregando...</p>
            ) : movements.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-6 italic">
                Nenhuma movimentação nos últimos 7 dias
              </p>
            ) : (
              movements.map((movement, idx) => (
                <div
                  key={`${movement.id}-${idx}`}
                  onClick={() => fetchChildDetails(movement.id)}
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                    movement.type === 'registered' 
                      ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                      : 'bg-red-50 border-red-200 hover:bg-red-100'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    movement.type === 'registered' ? 'bg-green-500' : 'bg-red-500'
                  }`}>
                    {movement.type === 'registered' ? (
                      <UserPlus className="h-3 w-3 text-white" />
                    ) : (
                      <UserMinus className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <ChildAvatar photo={movement.photo_url} name={movement.name} size="sm" zoomable={true} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 text-sm truncate hover:text-blue-600">{movement.name}</p>
                    <p className="text-xs text-slate-500">
                      {movement.type === 'registered' ? 'Cadastrado' : 'Inativado'} em {formatDateLabel(movement.date)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal de detalhes da criança */}
        <Dialog open={!!selectedMovementChild} onOpenChange={(open) => !open && setSelectedMovementChild(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedMovementChild && (
                  <>
                    <ChildAvatar 
                      photo={selectedMovementChild.photo_url} 
                      name={selectedMovementChild.name} 
                      size="lg"
                      zoomable={true}
                    />
                    <div>
                      <span className="text-lg">{selectedMovementChild.name}</span>
                      <p className="text-sm font-normal text-slate-500">
                        {selectedMovementChild.is_active ? 'Ativo' : 'Inativo'}
                      </p>
                    </div>
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {childDetailsLoading ? (
              <div className="py-8 text-center text-slate-500">Carregando...</div>
            ) : selectedMovementChild && (
              <div className="space-y-4 mt-2">
                {/* Responsável */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <Users className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Responsável</p>
                    <p className="font-medium text-slate-800">
                      {selectedMovementChild.guardian_name || '-'}
                    </p>
                    {selectedMovementChild.guardian_relationship && (
                      <p className="text-sm text-slate-600">
                        ({selectedMovementChild.guardian_relationship})
                      </p>
                    )}
                  </div>
                </div>

                {/* Informações Relevantes */}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Info className="h-4 w-4 text-amber-600" />
                    <p className="text-xs text-amber-700 uppercase tracking-wide font-medium">
                      Informações Relevantes
                    </p>
                  </div>
                  <p className="text-sm text-slate-700">
                    {selectedMovementChild.medical_info || 'Nenhuma informação relevante registrada'}
                  </p>
                </div>

                {/* Informações de matrícula */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 uppercase tracking-wide">Origem</p>
                    <p className="font-medium text-slate-800 text-sm">
                      {formatOrigin(selectedMovementChild.enrollment_origin)}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 uppercase tracking-wide">Turno</p>
                    <p className="font-medium text-slate-800 text-sm">
                      {selectedMovementChild.shift === 'Matutino' ? 'Manhã' : 'Tarde'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-600 uppercase tracking-wide">Dias</p>
                    <p className="font-medium text-slate-800 text-sm">
                      {formatDaysOfWeek(selectedMovementChild.days_of_week)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-600 uppercase tracking-wide">Idade</p>
                    <p className="font-medium text-slate-800 text-sm">
                      {selectedMovementChild.age ? `${selectedMovementChild.age} anos` : '-'}
                    </p>
                  </div>
                </div>

                {/* Motivo da inativação (se inativo) */}
                {!selectedMovementChild.is_active && selectedMovementChild.deactivation_reason && (
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Motivo da Inativação</p>
                    <p className="text-sm text-slate-700">
                      {selectedMovementChild.deactivation_reason}
                    </p>
                    {selectedMovementChild.deactivation_date && (
                      <p className="text-xs text-slate-500 mt-1">
                        Em {new Date(selectedMovementChild.deactivation_date).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  };

  // Render birthdays
  const renderBirthdays = () => {
    const allBirthdays = [
      ...(stats?.birthdays_children || []).map(b => ({ ...b, type: 'Criança' })),
      ...(stats?.birthdays_professionals || []).map(b => ({ ...b, type: 'Profissional' }))
    ];

    if (allBirthdays.length === 0) return null;

    // Sort by day of month
    const sortedBirthdays = [...allBirthdays].sort((a, b) => {
      const dayA = parseInt(a.birth_date?.split('-')[2] || '0', 10);
      const dayB = parseInt(b.birth_date?.split('-')[2] || '0', 10);
      return dayA - dayB;
    });

    // Format date as "XX de mês"
    const formatBirthdayFull = (dateStr) => {
      if (!dateStr) return '';
      const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
      const parts = dateStr.split('-');
      if (parts.length >= 3) {
        const day = parseInt(parts[2], 10);
        const month = parseInt(parts[1], 10) - 1;
        return `${day.toString().padStart(2, '0')} de ${months[month]}`;
      }
      return dateStr;
    };

    return (
      <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2 text-lg">
          <span>🎉🥳</span>
          Aniversariantes do Mês
        </h3>
        <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
          {sortedBirthdays.map((birthday, index) => (
            <div 
              key={index} 
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                birthday.is_today 
                  ? 'bg-amber-100 border-amber-400 shadow-md' 
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className={`flex-shrink-0 w-14 h-14 rounded-full border-3 flex items-center justify-center overflow-hidden ${
                birthday.is_today ? 'border-amber-500 bg-amber-50' : 'border-amber-300 bg-slate-100'
              }`} style={{ borderWidth: '3px' }}>
                {birthday.photo_url ? (
                  <img 
                    src={birthday.photo_url} 
                    alt={birthday.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Users className="h-6 w-6 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold flex items-center gap-1 flex-wrap ${birthday.is_today ? 'text-amber-900' : 'text-slate-800'}`}>
                  {birthday.is_today && <span>🎂</span>}
                  <span>{birthday.name}</span>
                  {birthday.is_today && <span>🎉</span>}
                </p>
                <p className={`text-sm ${birthday.is_today ? 'text-amber-700' : 'text-slate-500'}`}>
                  {birthday.type} - {formatBirthdayFull(birthday.birth_date)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D87A4E]"></div>
      </div>
    );
  }

  // Get box render function
  const getBoxRender = (boxId) => {
    switch (boxId) {
      case 'schedule': return renderSchedule();
      case 'birthdays': return renderBirthdays();
      case 'calendar': return renderCalendar();
      case 'tasks': return renderTasks();
      case 'absence_alerts': return renderAbsenceAlerts();
      case 'movements': return renderMovements();
      default: return null;
    }
  };

  // Sortable Box Component (para visualização no dashboard - não usado)
  const SortableBox = ({ id, children }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style} className="relative">
        {isEditMode && selectedUserId && (
          <div 
            {...attributes} 
            {...listeners}
            className="absolute -top-2 -left-2 z-10 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center cursor-grab active:cursor-grabbing shadow-lg"
          >
            <GripVertical className="h-4 w-4 text-white" />
          </div>
        )}
        {children}
      </div>
    );
  };

  // Sortable Order Item Component - para arrastar na configuração
  const SortableOrderItem = ({ id, label, index, isVisible }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.7 : 1,
      zIndex: isDragging ? 100 : 1,
    };

    return (
      <div 
        ref={setNodeRef} 
        style={style}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
          isVisible 
            ? 'bg-blue-50 border-blue-200' 
            : 'bg-slate-50 border-slate-200 opacity-50'
        } ${isDragging ? 'shadow-lg' : ''}`}
      >
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-blue-100 rounded"
        >
          <GripVertical className="h-4 w-4 text-slate-400" />
        </div>
        <span className={`text-sm font-medium ${isVisible ? 'text-blue-800' : 'text-slate-400'}`}>
          {index + 1}. {label}
        </span>
        <span className={`text-xs px-2 py-0.5 rounded ${isVisible ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
          {isVisible ? 'Visível' : 'Oculto'}
        </span>
      </div>
    );
  };

  // Filtered and ordered boxes - for current user's dashboard
  const orderedBoxes = dashboardConfig.box_order.filter(
    boxId => dashboardConfig.visible_boxes.includes(boxId)
  );
  
  // Filtered and ordered boxes - for user being edited by admin
  const editingOrderedBoxes = editingUserConfig 
    ? editingUserConfig.box_order.filter(boxId => editingUserConfig.visible_boxes.includes(boxId))
    : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-600 mt-1">Visão geral do sistema</p>
        </div>
        {isAdmin() && !isEditMode && (
          <button
            onClick={openAdminConfig}
            className="px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-2 border border-blue-200"
            data-testid="config-dashboards-btn"
          >
            <Settings className="h-4 w-4" />
            Configurar Dashboards
          </button>
        )}
      </div>

      {/* Admin Edit Mode - Configure OTHER users' dashboards */}
      {isEditMode && isAdmin() && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-blue-800">Configurar Dashboard de Usuários</h3>
            <button
              onClick={() => {
                setIsEditMode(false);
                setSelectedUserId(null);
                setEditingUserConfig(null);
              }}
              className="p-1 text-blue-600 hover:text-blue-800 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {!selectedUserId ? (
            <>
              <p className="text-sm text-blue-600 mb-3">Selecione um usuário para configurar seu dashboard:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {allUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => loadUserConfig(u.id)}
                    className="p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                  >
                    <p className="font-medium text-slate-800 truncate">{u.full_name || u.username}</p>
                    <p className="text-xs text-slate-500">{u.role}</p>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => {
                    setSelectedUserId(null);
                    setEditingUserConfig(null);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Voltar
                </button>
                <span className="text-sm text-slate-600">
                  Configurando: <strong>{allUsers.find(u => u.id === selectedUserId)?.full_name || 'Usuário'}</strong>
                </span>
              </div>
              
              <p className="text-sm text-blue-600 mb-3">Marque/desmarque para mostrar/ocultar boxes. Arraste para reordenar.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {ALL_BOX_IDS.map((id) => (
                  <label 
                    key={id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                      editingUserConfig?.visible_boxes?.includes(id) 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-slate-600 border border-slate-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={editingUserConfig?.visible_boxes?.includes(id) || false}
                      onChange={() => toggleBoxVisibility(id)}
                      className="sr-only"
                    />
                    <span className="text-sm">{boxDefinitions[id]?.label}</span>
                  </label>
                ))}
              </div>
              
              {/* Preview of user's dashboard layout with DRAG AND DROP */}
              <div className="bg-white rounded-lg p-3 mb-4">
                <p className="text-xs text-slate-500 mb-2">Arraste para reordenar (somente boxes visíveis):</p>
                <DndContext 
                  sensors={sensors} 
                  collisionDetection={closestCenter} 
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={editingUserConfig?.box_order || []} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {(editingUserConfig?.box_order || []).map((boxId, idx) => {
                        const isVisible = editingUserConfig?.visible_boxes?.includes(boxId);
                        return (
                          <SortableOrderItem 
                            key={boxId} 
                            id={boxId}
                            label={boxDefinitions[boxId]?.label}
                            index={idx}
                            isVisible={isVisible}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setSelectedUserId(null);
                    setEditingUserConfig(null);
                  }}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveUserDashboardConfig}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-1"
                >
                  <Check className="h-4 w-4" />
                  Salvar Configuração
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Stats Cards - SEMPRE visíveis */}
      {renderStatsCards()}

      {/* Modular Boxes - Layout masonry com CSS columns */}
      {!isEditMode && (
        <div className="lg:columns-2 gap-4 sm:gap-6 space-y-4 sm:space-y-6 [column-fill:_balance]">
          {orderedBoxes.map(boxId => (
            <div key={boxId} className="break-inside-avoid-column mb-4 sm:mb-6">
              {getBoxRender(boxId)}
            </div>
          ))}
        </div>
      )}

      {/* Calendar Event Detail Modal */}
      {selectedDateEvents && (
        <div 
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
          onClick={() => { setSelectedDateEvents(null); setSelectedDate(null); }}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl p-4 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-slate-600" />
                <h3 className="font-semibold text-slate-800">
                  {selectedDate && `${selectedDate} de ${monthName}`}
                </h3>
              </div>
              <button
                onClick={() => { setSelectedDateEvents(null); setSelectedDate(null); }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              {selectedDateEvents.map((event, idx) => (
                <div 
                  key={idx}
                  className={`p-3 rounded-lg border ${getEventColorLight(event)}`}
                >
                  <h4 className="font-medium">{event.title}</h4>
                  {event.description && (
                    <p className="text-sm mt-1 opacity-80">{event.description}</p>
                  )}
                  <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-white/50">
                    {getEventCategoryLabel(event)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Absence Child Detail Modal */}
      {selectedAbsenceChild && (
        <div 
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedAbsenceChild(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl p-4 max-w-sm w-full border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <ChildAvatar 
                photo={selectedAbsenceChild.photo_url} 
                name={selectedAbsenceChild.name} 
                size="lg"
                zoomable={true}
                className={`border-2 ${
                  selectedAbsenceChild.alert_type === 'unjustified' ? 'border-red-300' :
                  selectedAbsenceChild.alert_type === 'justified' ? 'border-orange-300' :
                  'border-amber-300'
                }`}
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-800 truncate">{selectedAbsenceChild.name}</h4>
                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                  selectedAbsenceChild.alert_type === 'unjustified' ? 'bg-red-100 text-red-700' :
                  selectedAbsenceChild.alert_type === 'justified' ? 'bg-orange-100 text-orange-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {selectedAbsenceChild.consecutive_absences} faltas consecutivas
                </span>
              </div>
              <button
                onClick={() => setSelectedAbsenceChild(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-slate-500 w-24 flex-shrink-0">Faltas:</span>
                <div className="flex flex-wrap gap-1.5">
                  {selectedAbsenceChild.unjustified_count > 0 && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                      {selectedAbsenceChild.unjustified_count} não justificada{selectedAbsenceChild.unjustified_count > 1 ? 's' : ''}
                    </span>
                  )}
                  {selectedAbsenceChild.justified_count > 0 && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                      {selectedAbsenceChild.justified_count} justificada{selectedAbsenceChild.justified_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <span className="text-slate-500 w-24 flex-shrink-0">Responsável:</span>
                <span className="text-slate-800 font-medium">
                  {selectedAbsenceChild.guardian_name || 'Não informado'}
                  {selectedAbsenceChild.guardian_relationship && (
                    <span className="text-slate-500 font-normal"> ({selectedAbsenceChild.guardian_relationship})</span>
                  )}
                </span>
              </div>

              <div className="flex items-start gap-2">
                <span className="text-slate-500 w-24 flex-shrink-0">Telefone:</span>
                <div className="flex items-center gap-2">
                  <span className="text-slate-800">{selectedAbsenceChild.guardian_phone || 'Não informado'}</span>
                  {selectedAbsenceChild.whatsapp_link && (
                    <a
                      href={selectedAbsenceChild.whatsapp_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg transition-colors"
                    >
                      <Phone className="h-3 w-3" />
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <span className="text-slate-500 w-24 flex-shrink-0">Frequenta:</span>
                <div className="flex flex-wrap gap-1">
                  {selectedAbsenceChild.days_of_week?.map(day => (
                    <span key={day} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {getDayLabel(day)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <span className="text-slate-500 w-24 flex-shrink-0">Turno:</span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                  {selectedAbsenceChild.shift === 'Matutino' ? 'Manhã' : 'Tarde'}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-100">
              <p className="text-xs text-slate-400">Clique fora para fechar</p>
            </div>
          </div>
        </div>
      )}

      {/* Completed Tasks History Modal */}
      {showCompletedHistory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="rounded-xl shadow-2xl p-5 max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
            style={{ 
              background: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)',
              border: '2px solid #FACC15'
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-amber-700" />
                <h3 className="font-semibold text-amber-900 text-lg">Tarefas Concluídas</h3>
              </div>
              <button
                onClick={() => setShowCompletedHistory(false)}
                className="p-1 text-amber-700 hover:text-amber-900 hover:bg-amber-200/50 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <label className="text-sm text-amber-800 mb-1 block">Filtrar por data:</label>
              <select
                value={selectedHistoryDate}
                onChange={(e) => {
                  setSelectedHistoryDate(e.target.value);
                  fetchHistoryTasks(e.target.value);
                }}
                className="w-full px-3 py-2 rounded-lg border border-amber-300 bg-white/70 text-amber-900 text-sm"
              >
                {completedDates.map(date => (
                  <option key={date} value={date}>{formatDateLabel(date)}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2">
              {historyTasks.length === 0 ? (
                <p className="text-amber-600 text-sm text-center py-8 italic">
                  Nenhuma tarefa concluída nesta data
                </p>
              ) : (
                historyTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-center gap-2 p-2 rounded-lg bg-white/50"
                  >
                    <div className="w-5 h-5 rounded border-2 bg-green-500 border-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <span className="flex-1 text-sm text-amber-800 line-through">
                      {task.text}
                    </span>
                  </div>
                ))
              )}
            </div>

            {completedDates.length === 0 && (
              <p className="text-amber-600 text-sm text-center py-8 italic">
                Nenhuma tarefa concluída ainda
              </p>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
